/**
 * <s-load-more> — "Show more" for paginated product grids.
 *
 * Markup contract:
 *   <s-load-more data-grid="#product-grid">
 *     <p data-load-more-status aria-live="polite">Showing 20 of 119</p>
 *     <a data-load-more-link href="?page=2">Show more</a>
 *   </s-load-more>
 *
 * The link is a real next-page URL, so it works without JavaScript. With JavaScript, clicking it
 * fetches that page, appends its grid items to the current grid, moves focus to the first new
 * product, and swaps in the fetched page's status text and next link (or removes the link on the last page).
 */
class SLoadMore extends HTMLElement {
  #busy = false;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  #onClick = async (event) => {
    const link = event.target.closest('[data-load-more-link]');
    if (!link || !this.contains(link)) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
    event.preventDefault();
    if (this.#busy) return;

    const gridSelector = this.dataset.grid;
    const grid = gridSelector ? document.querySelector(gridSelector) : null;
    if (!grid) {
      window.location.href = link.href;
      return;
    }

    this.#busy = true;
    link.setAttribute('aria-busy', 'true');
    link.classList.add('opacity-60', 'pointer-events-none');

    try {
      const response = await fetch(link.href, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');

      const nextGrid = doc.querySelector(gridSelector);
      const newItems = nextGrid ? Array.from(nextGrid.children) : [];
      const firstNew = newItems[0];
      for (const item of newItems) grid.append(document.adoptNode(item));

      const nextControl = doc.querySelector('s-load-more');
      const nextStatus = nextControl?.querySelector('[data-load-more-status]');
      const status = this.querySelector('[data-load-more-status]');
      if (status && nextStatus) status.textContent = nextStatus.textContent;
      const bar = this.querySelector('[data-load-more-bar]');
      const nextBar = nextControl?.querySelector('[data-load-more-bar]');
      if (bar && nextBar) bar.style.width = nextBar.style.width;

      const nextLink = nextControl?.querySelector('[data-load-more-link]');
      if (nextLink) {
        link.href = nextLink.getAttribute('href');
        link.removeAttribute('aria-busy');
        link.classList.remove('opacity-60', 'pointer-events-none');
      } else {
        link.remove();
      }

      // Keyboard and screen reader users continue from the first newly added product.
      const focusTarget = firstNew?.querySelector('a[href], button, [tabindex]');
      focusTarget?.focus({ preventScroll: true });
    } catch (error) {
      // Fall back to the regular next page if anything goes wrong.
      window.location.href = link.href;
      return;
    } finally {
      this.#busy = false;
    }
  };
}

if (!customElements.get('s-load-more')) customElements.define('s-load-more', SLoadMore);
